import { createBrowserRouter, RouterProvider, Navigate } from 'react-router';
import { Suspense, lazy } from 'react';
import { EvoluProvider } from '@evolu/react';
import { AppShell } from '@/components/layout/AppShell';
import { AuthProvider } from '@/contexts/AuthProvider';
import { MnemonicProvider } from '@/contexts/MnemonicProvider';
import { AuthGate } from '@/features/auth/components/AuthGate';
import { createDatabase } from '@/db/database';
import { StartupTokenSweep } from '@/features/cashu/components/StartupTokenSweep';
import { useStartupSweepDataSource } from '@/features/cashu/hooks/useStartupSweepDataSource';
import { useNwcConnectionsDataSource } from '@/features/lightning/hooks/useNwcConnectionsDataSource';
import { useNwcConnections } from '@/features/lightning/hooks/useNwcConnections';
import { useNwcAutoReconnect } from '@/features/lightning/hooks/useNwcAutoReconnect';
import { useNwcConnectionTimestamp } from '@/features/lightning/hooks/useNwcConnectionTimestamp';
import { useLightningHistorySync } from '@/features/lightning/hooks/useLightningHistorySync';
import { useLightningHistoryDataSource } from '@/features/lightning/hooks/useLightningHistoryDataSource';
import { useLightningTransactionsDataSource } from '@/features/lightning/hooks/useLightningTransactionsDataSource';
import { NwcClientProvider, useSharedNwcClient } from '@/contexts/NwcClientProvider';
import { ActiveConnectionProvider } from '@/contexts/ActiveConnectionProvider';
import { MotionProvider } from '@/contexts/MotionProvider';
import { PageTitleProvider } from '@/contexts/PageTitleContext';
import { OfflineBanner } from '@/components/OfflineBanner';
import { useNpubCashBackgroundClaim } from '@/hooks/useNpubCashBackgroundClaim';
import { useCashuReceiveAdapter } from '@/features/cashu/hooks/useCashuReceiveAdapter';
import { useTransactionContactLinker } from '@/features/contacts/hooks/useTransactionContactLinker';
import { useContactsDataSource } from '@/features/contacts/hooks/useContactsDataSource';
import { ROUTES } from '@/types/routes';

const DashboardPage = lazy(() => import('@/features/wallet/pages/DashboardPage'));
const SettingsPage = lazy(() => import('@/features/settings/pages/SettingsPage'));
const SecuritySettingsPage = lazy(() => import('@/features/settings/pages/SecuritySettingsPage'));
const BackupPage = lazy(() => import('@/features/backup/pages/BackupPage'));
const SendPage = lazy(() => import('@/features/send-receive/pages/SendPage'));
const ReceivePage = lazy(() => import('@/features/send-receive/pages/ReceivePage'));
const MintsPage = lazy(() => import('@/features/cashu/pages/MintsPage'));
const MintDetailPage = lazy(() => import('@/features/cashu/pages/MintDetailPage'));
const MintMeltPage = lazy(() => import('@/features/cashu/pages/MintMeltPage'));
const WalletsListPage = lazy(() => import('@/features/wallet/pages/WalletsListPage'));
const EcashWalletDetailPage = lazy(() => import('@/features/wallet/pages/EcashWalletDetailPage'));
const NwcWalletDetailPage = lazy(() => import('@/features/wallet/pages/NwcWalletDetailPage'));
const ConnectionsPage = lazy(() => import('@/features/lightning/pages/ConnectionsPage'));
const AddConnectionPage = lazy(() => import('@/features/lightning/pages/AddConnectionPage'));
const ConnectionDetailPage = lazy(() => import('@/features/lightning/pages/ConnectionDetailPage'));
const HistoryPage = lazy(() => import('@/features/lightning/pages/HistoryPage'));
const ContactsPage = lazy(() => import('@/features/contacts/pages/ContactsPage'));
const ContactDetailPage = lazy(() => import('@/features/contacts/pages/ContactDetailPage'));
const VanityAddressPage = lazy(() => import('@/features/contacts/pages/VanityAddressPage'));

/** Evolu database instance created once at app startup. */
const evolu = createDatabase();

const router = createBrowserRouter([
  {
    element: <AppShell />,
    children: [
      {
        path: ROUTES.DASHBOARD,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <DashboardPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.SEND,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <SendPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.RECEIVE,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ReceivePage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.SETTINGS,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <SettingsPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.SETTINGS_SECURITY,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <SecuritySettingsPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.BACKUP,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <BackupPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.BRIDGE,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <MintMeltPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.CONNECTIONS,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ConnectionsPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.CONNECTIONS_ADD,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <AddConnectionPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.CONNECTION_DETAIL,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ConnectionDetailPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.HISTORY,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <HistoryPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.CONTACTS,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ContactsPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.CONTACT_DETAIL,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ContactDetailPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.SETTINGS_MINTS,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <MintsPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.WALLETS,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <WalletsListPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.WALLETS_ECASH,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <EcashWalletDetailPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.WALLETS_ECASH_MINT,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <MintDetailPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.WALLETS_LIGHTNING,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <NwcWalletDetailPage />
          </Suspense>
        ),
      },
      {
        path: '/mints',
        element: <Navigate to="/wallets/ecash" replace />,
      },
      {
        path: ROUTES.VANITY_ADDRESS,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <VanityAddressPage />
          </Suspense>
        ),
      },
    ],
  },
]);

/** Loading fallback shown while lazy-loaded pages are fetching */
function LoadingFallback(): React.ReactElement {
  return (
    <div className="flex items-center justify-center py-12">
      <p className="text-muted-foreground">Loading...</p>
    </div>
  );
}

/**
 * Wired startup token sweep that uses real Evolu data.
 *
 * Must be a React component (not a bare hook call) because
 * useStartupSweepDataSource uses Evolu hooks requiring EvoluProvider context.
 */
function StartupTokenSweepWired(): React.ReactElement | null {
  const { dataSource, walletFactory } = useStartupSweepDataSource();
  return <StartupTokenSweep dataSource={dataSource} walletFactory={walletFactory} />;
}

/**
 * Wired NWC auto-reconnect that reads connections from Evolu.
 *
 * Must be a React component because it uses Evolu hooks (useNwcConnectionsDataSource)
 * and the NWC client hook, both requiring provider context.
 */
function NwcAutoReconnectWired(): React.ReactElement | null {
  const { dataSource } = useNwcConnectionsDataSource();
  const { connections, activeConnection } = useNwcConnections(dataSource);
  const nwcClient = useSharedNwcClient();

  useNwcAutoReconnect({ connections, connectFn: nwcClient.connect });
  useNwcConnectionTimestamp({
    status: nwcClient.status,
    activeConnection,
    dataSource,
  });

  return null;
}

/**
 * Wired Lightning history sync that fetches past transactions from the NWC wallet.
 *
 * Automatically syncs when the shared NWC client connects. Deduplicates
 * against existing Evolu records by payment hash.
 */
function LightningHistorySyncWired(): React.ReactElement | null {
  const nwcClient = useSharedNwcClient();
  const { dataSource: txDataSource } = useLightningTransactionsDataSource();
  // Use unfiltered data source for sync — needs ALL transactions for dedup and backfill
  const { dataSource: historyDataSource } = useLightningHistoryDataSource();
  const { dataSource: connectionsDataSource } = useNwcConnectionsDataSource();
  const { activeConnection } = useNwcConnections(connectionsDataSource);

  useLightningHistorySync({
    client: nwcClient.client,
    connectionStatus: nwcClient.status,
    dataSource: txDataSource,
    existingTransactions: historyDataSource.transactions,
    connectionId: activeConnection?.id ?? null,
    txRowIdMap: historyDataSource.txRowIdMap,
  });

  return null;
}

/**
 * Wired transaction-contact re-linker that runs on startup.
 *
 * Re-links orphaned transaction contactId references after restore
 * using domain identifier matching (Lightning Address, Nostr pubkey, BOLT12).
 */
function TransactionContactLinkerWired(): React.ReactElement | null {
  const contactsDs = useContactsDataSource();
  const { dataSource: historyDs } = useLightningHistoryDataSource();

  useTransactionContactLinker({
    transactions: historyDs.transactions.map((tx) => ({
      id: tx.id,
      contactId: tx.contactId,
      lightningAddress: tx.lightningAddress,
    })),
    contacts: contactsDs.contacts,
    updateTransactionContact: () => {
      // Placeholder: Evolu update wired when transaction schema has contactId column
    },
  });

  return null;
}

/**
 * Background npub.cash token claimer.
 * Polls every 30s for unclaimed tokens and redeems them via Cashu receive.
 */
function NpubCashBackgroundClaimWired(): React.ReactElement | null {
  const cashuAdapter = useCashuReceiveAdapter();
  useNpubCashBackgroundClaim(cashuAdapter.receive);
  return null;
}

/**
 * Root application component.
 *
 * Sets up providers (Evolu, Auth, Mnemonic), AuthGate for routing,
 * StartupTokenSweepWired for CASH-11 token verification with real Evolu data,
 * NwcAutoReconnectWired for LN-09 auto-reconnect on app launch,
 * LightningHistorySyncWired for backfilling transaction history from NWC,
 * and React Router with lazy-loaded pages and the app shell layout.
 */
export function App(): React.ReactElement {
  return (
    <MotionProvider>
      <EvoluProvider value={evolu}>
        <AuthProvider>
          <MnemonicProvider>
            <AuthGate>
              <ActiveConnectionProvider>
                <NwcClientProvider>
                  <OfflineBanner />
                  <StartupTokenSweepWired />
                  <NwcAutoReconnectWired />
                  <LightningHistorySyncWired />
                  <TransactionContactLinkerWired />
                  <NpubCashBackgroundClaimWired />
                  <PageTitleProvider>
                    <RouterProvider router={router} />
                  </PageTitleProvider>
                </NwcClientProvider>
              </ActiveConnectionProvider>
            </AuthGate>
          </MnemonicProvider>
        </AuthProvider>
      </EvoluProvider>
    </MotionProvider>
  );
}
